import { devLog } from '../../../utils/devLog';
/**
 * DrillsModule - Main drills and training management module
 * 
 * MODULE HEADERS:
 * - Container: mb-8
 * - Title: text-2xl font-bold text-gray-900
 * - Subtitle: mt-1 text-sm text-gray-500
 * 
 * CONTENT CARDS:
 * - Container: bg-white shadow rounded-lg
 * - Header: px-6 py-5 border-b border-gray-200
 * - Header text: text-lg font-medium text-gray-900
 * - Body: p-6 or divide-y divide-gray-200 for lists
 * 
 * BUTTONS:
 * - Primary: bg-cyan-600 hover:bg-cyan-700 text-white
 * - Secondary: bg-white border-gray-300 text-gray-700
 * 
 * SPACING:
 * - Between major sections: mb-8
 * - Within cards: space-y-4 or similar
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Download } from 'lucide-react';
import TabNavigation from '../TabNavigation';
// FT-357: shell-level back handler — register a "back to dashboard" handler
// so mobile users always see a Back button in the bottom bar.
import { useSetBackHandler } from '../../../contexts/NavigationBackContext';
import { useVessel } from 'contexts/VesselContext';
import { useCrew } from '../../../hooks/useCrew';
import { useDrillsData } from '../../../hooks/domain/useDrillsData';
import { useDrillsConfig } from '../../../hooks/domain/drills/useDrillsConfig';
import { filterTombstoned } from '../../../sync/tombstone';  // FT-458: hide soft-deleted drills from display/counts/tabs
import { useCustomDialogs } from '../../../hooks/useCustomDialogs';
import { useRequirementsBadge } from '../../../hooks/useRequirementsBadge';
import { usePermissions } from '../../../hooks/usePermissions';
import SafetyDrillsTab from './drills/SafetyDrillsTab';
import TrainingRecordsTab from './drills/TrainingRecordsTab';
import DrillHistoryTab from './drills/DrillHistoryTab';
import DrillRecordFormClean from './drills/DrillRecordFormClean';
import DrillDetailView from './drills/DrillDetailView';
import RecordTrainingView from './drills/RecordTrainingView';
import TrainingDetailView from './drills/TrainingDetailView';
import DrillSettingsView from './drills/DrillSettingsView';
import DrillExportModal from './drills/DrillExportModal';
import { addAuditEntry, AUDIT_ACTIONS, AUDIT_MODULES } from '../../../utils/audit';
import { useMainLogbook } from '../../../hooks/domain/operations/useMainLogbook';

const DrillsModule = ({ setActiveModule }) => {
  // FT-357: register a shell-level "back to dashboard" handler so mobile
  // users always see a Back button in the bottom bar for this module.
  useSetBackHandler(
    useCallback(() => {
      if (setActiveModule) setActiveModule('dashboard');
    }, [setActiveModule])
  );

  // Main Logbook hook for auto-logging drill completions
  const { logDrill } = useMainLogbook();

  // Get current user for audit trails and role checks
  const { currentUser, can } = usePermissions();
  // FT-394: use the RBAC gate (includes vessel-assignment check) instead of raw role checks
  const canSignOff = can('sign', 'drills');

  const [activeTab, setActiveTab] = useState('drills');
  const [showAddDrill, setShowAddDrill] = useState(false);
  const [selectedDrill, setSelectedDrill] = useState(null);
  const [showDrillDetail, setShowDrillDetail] = useState(false);
  const [drillEditReason, setDrillEditReason] = useState('');
  const [preselectedDrillType, setPreselectedDrillType] = useState(null);
  const [editingDrill, setEditingDrill] = useState(null);
  const [showAddTraining, setShowAddTraining] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [showTrainingDetail, setShowTrainingDetail] = useState(false);
  const [trainingEditReason, setTrainingEditReason] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Loading states for async operations
  const [isSavingDrill, setIsSavingDrill] = useState(false);
  const [isDeletingDrill, setIsDeletingDrill] = useState(false);
  const [isSavingTraining, setIsSavingTraining] = useState(false);

  // SYNC monitoring for Anchor Mode compliance (dev only)
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const handler = (e) => {
        if (e.detail?.module === 'drills' || e.detail?.module === 'onboard_training') {
          devLog('[SYNC EVENT - Drills]', e.detail);
        }
      };
      window.addEventListener('sr:storage:set', handler);
      return () => window.removeEventListener('sr:storage:set', handler);
    }
  }, []);
  
  const { currentVesselId, getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  
  // Get vessel config for requirements badge
  const vesselConfig = currentVessel ? {
    vesselName: currentVessel.name,
    vesselType: currentVessel.type,
    engineType: currentVessel.engineType || 'diesel',
    category: currentVessel.category,
    tonnage: currentVessel.tonnage,
    operatingArea: currentVessel.operatingArea,
    carriesPassengers: currentVessel.carriesPassengers
  } : null;

  // Requirements badge integration (sync disabled - using drill register as source of truth)
  const { hasUpdates, checking } = useRequirementsBadge('drills', vesselConfig);
  
  // Use the NEW useCrew hook for crew data
  const { crew, loading: crewLoading } = useCrew();

  // FT-112 REWRITE: Get drill list directly from config (single source of truth)
  // React handles reactivity automatically - no events/fingerprints needed
  const { drillList } = useDrillsConfig();
  
  // Use domain hook for drills data
  const {
    drills,
    onboardTraining,
    requirements: vesselRequirementsObj,
    setDrills: saveDrills,
    setOnboardTraining: saveOnboardTraining,
    getOverdueDrills,
    getCompliancePercentage,
    drillTypes,
    normalizeDrillRequirement,
    approveDrill  // FT-06d P2: Sign-off function
  } = useDrillsData();

  // FT-458: display/count chokepoint — tombstoned (soft-deleted) drills are
  // hidden from every read (tabs, stats, empty-check). `drills` (raw, tombstones
  // intact) is still used for all WRITES so the tombstone is preserved +
  // propagates to cloud/other devices.
  const visibleDrills = filterTombstoned(drills);

  const { showAlert, showConfirm, AlertDialog, ConfirmDialog } = useCustomDialogs();

  // Handle navigation from other modules
  // TODO: Re-enable when targetTab is passed as prop
  // useEffect(() => {
  //   if (targetTab && ['drills', 'training', 'history'].includes(targetTab)) {
  //     setActiveTab(targetTab);
  //     clearTargetTab();
  //   }
  // }, [targetTab, clearTargetTab]);

  // Get vessel requirements from domain hook
  const vesselRequirements = Object.values(vesselRequirementsObj || {});

  const handleBackFromDrillDetail = useCallback(() => {
    setShowDrillDetail(false);
    setSelectedDrill(null);
    setDrillEditReason('');
  }, []);

  const handleCloseRecordDrillForm = useCallback(() => {
    setShowAddDrill(false);
    setPreselectedDrillType('');
    setEditingDrill(null);
    setDrillEditReason('');
  }, []);

  const handleBackFromTrainingDetail = useCallback(() => {
    setShowTrainingDetail(false);
    setSelectedTraining(null);
    setTrainingEditReason('');
  }, []);

  const handleBackFromRecordTraining = useCallback(() => {
    setShowAddTraining(false);
    setSelectedTraining(null);
    setTrainingEditReason('');
  }, []);

  const handleBackFromDrillSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  // Tabs - Training tab visibility controlled by enableTrainingSection setting
  const showTrainingTab = currentVessel?.enableTrainingSection ?? true; // Default to true for backward compatibility
  const tabs = [
    { id: 'drills', label: 'Safety Drills' },
    ...(showTrainingTab ? [{ id: 'training', label: 'Onboard Training' }] : []),
    { id: 'history', label: 'Drill History' }
  ];

  // Handle sync requirements - DISABLED (using drill register as source of truth)
  const handleSyncRequirements = async () => {
    // No longer needed - drill register is automatically synced

  };

  // Generate drill ID in format DR001-25, DR002-25, etc.
  const generateDrillId = () => {
    const year = new Date().getFullYear().toString().slice(-2); // Get last 2 digits of year
    
    // Get all existing drill IDs for this year
    const currentYearDrills = drills.filter(drill => {
      if (!drill.id || !drill.id.startsWith('DR')) return false;
      const idYear = drill.id.split('-')[1];
      return idYear === year;
    });
    
    // Find the highest number
    let highestNumber = 0;
    currentYearDrills.forEach(drill => {
      const match = drill.id.match(/DR(\d{3})-/);
      if (match) {
        const num = parseInt(match[1]);
        if (num > highestNumber) highestNumber = num;
      }
    });
    
    // Generate new ID with next number
    const nextNumber = (highestNumber + 1).toString().padStart(3, '0');
    return `DR${nextNumber}-${year}`;
  };

  // Save new drill
  const handleSaveDrill = async (drillRecord) => {
    // Validation
    if (!drillRecord) {
      showAlert('Error', 'Invalid drill record', 'error');
      return;
    }

    if (!drillRecord.type || typeof drillRecord.type !== 'string') {
      showAlert('Validation Error', 'Please select a drill type', 'error');
      return;
    }

    if (!drillRecord.date || isNaN(new Date(drillRecord.date).getTime())) {
      showAlert('Validation Error', 'Please enter a valid drill date', 'error');
      return;
    }

    if (!drillRecord.participants || !Array.isArray(drillRecord.participants) || drillRecord.participants.length === 0) {
      showAlert('Validation Error', 'Please select at least one participant', 'error');
      return;
    }

    // Normalize outcome to capitalized format for consistency
    // Handles both lowercase ('successful') and capitalized ('Successful') input
    const normalizedOutcome = drillRecord.outcome
      ? drillRecord.outcome.charAt(0).toUpperCase() + drillRecord.outcome.slice(1).toLowerCase()
      : '';

    // Case-insensitive validation - works with any case input
    const validOutcomes = ['successful', 'partial', 'failed'];
    if (!normalizedOutcome || !validOutcomes.includes(normalizedOutcome.toLowerCase())) {
      showAlert('Validation Error', 'Please select a valid outcome', 'error');
      return;
    }

    // Check if we're editing an existing drill
    const isEditing = editingDrill && editingDrill.id;

    // Build drill data with audit trail
    let drillData;

    if (isEditing) {
      // EDIT MODE: Add audit trail
      const userName = currentUser?.name || 'Unknown User';
      const vesselName = currentVessel?.name || 'Unknown Vessel';
      const reason = drillEditReason || 'Record updated';

      // Calculate what changed
      const oldDrill = drills.find(d => d.id === editingDrill.id);
      const changes = [];

      // Track key fields that changed
      const fieldsToTrack = ['type', 'date', 'time', 'location', 'outcome', 'notes', 'participants', 'deficiencies', 'correctiveActions'];
      fieldsToTrack.forEach(field => {
        if (JSON.stringify(oldDrill?.[field]) !== JSON.stringify(drillRecord[field])) {
          changes.push({
            field,
            oldValue: oldDrill?.[field],
            newValue: drillRecord[field]
          });
        }
      });

      // Generate simple summary
      let summary = 'Text updated';
      if (changes.length === 1) {
        const fieldName = changes[0].field
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .trim();
        summary = `${fieldName} updated`;
      } else if (changes.length > 1) {
        summary = `${changes.length} fields updated`;
      }

      // Create audit entry
      const auditEntry = {
        id: `audit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        timestamp: new Date().toISOString(),
        user: userName,
        vessel: vesselName,
        action: 'updated',
        summary,
        changes,
        reason
      };

      drillData = {
        ...drillRecord,
        outcome: normalizedOutcome,
        id: editingDrill.id,
        createdAt: oldDrill?.createdAt || new Date().toISOString(),
        createdBy: oldDrill?.createdBy || userName,
        updatedAt: new Date().toISOString(),
        updatedBy: userName,
        vesselName: vesselName,
        auditLog: [...(oldDrill?.auditLog || []), auditEntry],
        // FT-394 FIX A: preserve sign-off state so editing a drill doesn't wipe it
        // from the sign-off queue. These are set AFTER the form spread so the form
        // cannot overwrite them.
        reviewStatus: oldDrill?.reviewStatus ?? 'pending',
        reviewedBy: oldDrill?.reviewedBy ?? null,
        reviewedByRole: oldDrill?.reviewedByRole ?? null,
        reviewedAt: oldDrill?.reviewedAt ?? null,
        reviewComments: oldDrill?.reviewComments ?? null
      };
    } else {
      // CREATE MODE: Initialize audit trail
      const userName = currentUser?.name || 'Unknown User';
      const vesselName = currentVessel?.name || 'Unknown Vessel';
      const now = new Date().toISOString();

      drillData = {
        ...drillRecord,
        outcome: normalizedOutcome,
        id: generateDrillId(),
        createdAt: now,
        createdBy: userName,
        vesselName: vesselName,
        auditLog: [{
          id: `audit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          timestamp: now,
          user: userName,
          vessel: vesselName,
          action: 'created',
          summary: 'Record created',
          changes: [],
          reason: 'Initial record creation'
        }],
        // FT-394: sign-off state machine.
        // signOffNow=true (skipper ticks toggle) → immediately approved.
        // signOffNow=false (default) → pending, awaiting skipper review via detail view.
        // FT-06e: Include reviewedByRole for "Name (Role)" display
        ...(drillRecord.signOffNow ? {
          reviewedBy: userName,
          reviewedByRole: currentUser?.role || '',
          reviewedAt: now,
          reviewStatus: 'approved',
          reviewComments: ''
        } : {
          reviewStatus: 'pending',   // FT-394: mandatory — all new drills need sign-off
          reviewedBy: null,
          reviewedByRole: null,
          reviewedAt: null,
          reviewComments: null
        })
      };
    }

    setIsSavingDrill(true);
    try {
      const updatedDrills = isEditing
        ? drills.map(d => d.id === editingDrill.id ? drillData : d)
        : [...drills, drillData];
      const success = await saveDrills(updatedDrills);

      if (success) {
        addAuditEntry({
          action: isEditing ? AUDIT_ACTIONS.UPDATE : AUDIT_ACTIONS.CREATE,
          module: AUDIT_MODULES.DRILL,
          targetId: drillData.id,
          summary: isEditing ? `Updated ${drillRecord.type} drill` : `Recorded ${drillRecord.type} drill`,
          details: {
            drillType: drillRecord.type,
            outcome: drillData.outcome,
            participantCount: drillRecord.participants?.length || 0
          }
        });

        // Auto-log new drills to Official Logbook (not edits)
        if (!isEditing) {
          await logDrill({
            drillId: drillData.id,
            drillName: drillRecord.type,
            type: drillRecord.type,
            timestamp: new Date().toISOString(),
            completedAt: drillRecord.date,
            participants: drillRecord.participants,
            participantCount: drillRecord.participants?.length || 0,
            duration: drillRecord.duration,
            outcome: drillData.outcome,
            notes: drillRecord.notes
          });

          // FT-06d P2b: Sign-off fields are now included directly in drillData above
          // (removed approveDrill call - it couldn't find the drill since state hadn't updated yet)
        }

        showAlert(
          isEditing ? 'Drill Updated' : 'Drill Recorded',
          isEditing
            ? `${drillRecord.type} drill has been updated successfully.`
            : `${drillRecord.type} drill has been recorded${drillRecord.signOffNow ? ' and signed off' : ''} successfully.`,
          'success'
        );
        setShowAddDrill(false);
        setPreselectedDrillType(null);
        setEditingDrill(null);
        setDrillEditReason('');
      } else {
        // Save returned false - show error and keep modal open for retry
        showAlert(
          'Save Failed',
          'Failed to save drill record. Please try again or contact support if the problem persists.',
          'error'
        );

      }
    } catch (error) {
      // Catch any unexpected errors during save
      showAlert(
        'Save Error',
        `An unexpected error occurred while saving the drill: ${error.message}`,
        'error'
      );

    } finally {
      setIsSavingDrill(false);
    }
  };

  // Update existing drill
  const handleUpdateDrill = async (updatedDrill) => {
    const updatedDrills = drills.map(drill => 
      drill.id === updatedDrill.id ? updatedDrill : drill
    );

    const success = await saveDrills(updatedDrills);
    if (success) {
      showAlert(
        'Drill Updated',
        'Drill record has been updated successfully.',
        'success'
      );
      setShowDrillDetail(false);
      setSelectedDrill(null);
    }
  };

  // Delete drill
  const handleDeleteDrill = async (drillId) => {
    const confirmed = await showConfirm(
      'Delete Drill Record',
      'Are you sure you want to delete this drill record? This action cannot be undone.',
      'Delete',
      'Cancel',
      'error'
    );

    if (!confirmed) return;

    setIsDeletingDrill(true);
    try {
      // FT-458 SAFETY-CRITICAL: soft-delete via tombstone, not hard-filter.
      // Hard-removing the item lets the still-present cloud copy re-seed it on
      // the next sync (cloud∪local union has no set-difference) → resurrection.
      // Stamp `deleted_at` (+ `deleted_by`) and KEEP it in the array (mirrors
      // shipped FT-175 crew) so the deletion propagates + is filtered from reads.
      const nowIso = new Date().toISOString();
      const deletedBy = currentUser?.name || 'Unknown User';
      const updatedDrills = drills.map(drill =>
        drill.id === drillId
          ? { ...drill, deleted_at: nowIso, deleted_by: deletedBy, updatedAt: Date.now() }
          : drill
      );
      const success = await saveDrills(updatedDrills);

      if (success) {
        showAlert(
          'Drill Deleted',
          'Drill record has been deleted.',
          'info'
        );
        setShowDrillDetail(false);
        setSelectedDrill(null);
      } else {
        showAlert(
          'Delete Failed',
          'Failed to delete drill record. Please try again.',
          'error'
        );
      }
    } catch (error) {
      showAlert(
        'Delete Error',
        `An unexpected error occurred: ${error.message}`,
        'error'
      );
    } finally {
      setIsDeletingDrill(false);
    }
  };

  // Save training record
  const handleSaveTraining = async (trainingRecord) => {
    setIsSavingTraining(true);
    try {
      let updatedTraining;
      let isEdit = false;

      // Check if we're editing an existing record
      if (selectedTraining?.id) {
        // EDIT MODE: Update existing record with audit trail
        isEdit = true;

        // Get user and vessel info
        const userName = currentUser?.name || 'Unknown User';
        const vesselName = currentVessel?.name || 'Unknown Vessel';
        const reason = trainingEditReason || 'Record updated';

        // Calculate what changed
        const oldRecord = onboardTraining.find(t => t.id === selectedTraining.id);
        const changes = [];

        // Track key fields that changed
        const fieldsToTrack = ['trainer', 'topic', 'date', 'duration', 'competencyLevel', 'description', 'trainees'];
        fieldsToTrack.forEach(field => {
          if (JSON.stringify(oldRecord?.[field]) !== JSON.stringify(trainingRecord[field])) {
            changes.push({
              field,
              oldValue: oldRecord?.[field],
              newValue: trainingRecord[field]
            });
          }
        });

        // Generate simple summary
        let summary = 'Text updated';
        if (changes.length === 1) {
          const fieldName = changes[0].field
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
          summary = `${fieldName} updated`;
        } else if (changes.length > 1) {
          summary = `${changes.length} fields updated`;
        }

        // Create audit entry
        const auditEntry = {
          id: `audit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          timestamp: new Date().toISOString(),
          user: userName,
          vessel: vesselName,
          action: 'updated',
          summary,
          changes,
          reason
        };

        // Update record with audit trail
        const updatedRecord = {
          ...trainingRecord,
          id: selectedTraining.id, // Keep existing ID!
          createdAt: oldRecord?.createdAt || new Date().toISOString(),
          createdBy: oldRecord?.createdBy,
          updatedAt: new Date().toISOString(),
          updatedBy: userName,
          auditLog: [...(oldRecord?.auditLog || []), auditEntry]
        };

        // Replace old record with updated one
        updatedTraining = onboardTraining.map(t =>
          t.id === selectedTraining.id ? updatedRecord : t
        );
      } else {
        // CREATE MODE: New record
        const userName = currentUser?.name || 'Unknown User';
        const vesselName = currentVessel?.name || 'Unknown Vessel';

        const newTraining = {
          ...trainingRecord,
          id: `TRAIN${Date.now()}`,
          createdAt: new Date().toISOString(),
          createdBy: userName,
          auditLog: [{
            id: `audit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
            timestamp: new Date().toISOString(),
            user: userName,
            vessel: vesselName,
            action: 'created',
            summary: 'Record created',
            changes: [],
            reason: 'Initial record creation'
          }]
        };

        updatedTraining = [...onboardTraining, newTraining];
      }

      const success = await saveOnboardTraining(updatedTraining);

      if (success) {
        // Note: Training records are tracked separately, not in the Official Logbook
        // Only safety drills go to the Main Logbook for regulatory compliance

        showAlert(
          isEdit ? 'Training Updated' : 'Training Recorded',
          isEdit
            ? 'Training record has been updated successfully.'
            : 'Onboard training has been recorded successfully.',
          'success'
        );
        setShowAddTraining(false);
        setSelectedTraining(null);
        setTrainingEditReason('');
      } else {
        showAlert(
          'Save Failed',
          'Failed to save training record. Please try again.',
          'error'
        );
      }
    } catch (error) {
      showAlert(
        'Save Error',
        `An unexpected error occurred: ${error.message}`,
        'error'
      );
    } finally {
      setIsSavingTraining(false);
    }
  };

  // Handle opening drill modal with preselected type
  const handleOpenDrillModal = (drillType = null) => {
    setPreselectedDrillType(drillType);
    setShowAddDrill(true);
  };

  // Handle export report - now opens modal instead of direct export
  const handleExportReport = async () => {
    if (visibleDrills.length === 0) {  // FT-458: ignore soft-deleted drills
      showAlert(
        'No Drill Records',
        'There are no drill records to export.',
        'info'
      );
      return;
    }

    setShowExportModal(true);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'drills':
        return (
          <SafetyDrillsTab
            drills={visibleDrills}
            vesselRequirements={vesselRequirements}
            drillList={drillList}
            normalizeDrillRequirement={normalizeDrillRequirement}
            onRecordDrill={handleOpenDrillModal}
            onViewDrill={(drill) => {
              setSelectedDrill(drill);
              setShowDrillDetail(true);
            }}
            onHistory={() => setActiveTab('history')}
            onSettings={() => setShowSettings(true)}
            onExport={handleExportReport}  // FT-387: wire overview-tab Export → DrillExportModal
          />
        );
      case 'training':
        return (
          <TrainingRecordsTab
            trainingRecords={onboardTraining}
            crew={crew}
            onAddTraining={() => setShowAddTraining(true)}
            onViewTraining={(training) => {
              setSelectedTraining(training);
              setShowTrainingDetail(true);
            }}
          />
        );
      case 'history':
        return (
          <DrillHistoryTab
            drills={visibleDrills}
            onViewDrill={(drill) => {
              setSelectedDrill(drill);
              setShowDrillDetail(true);
            }}
            onExport={handleExportReport}  // FT-387: wire history-tab Export → DrillExportModal
          />
        );
      default:
        return null;
    }
  };

  // FT-459: Compute the active view into `body` rather than returning early.
  // The shared confirm/alert dialog hosts (<ConfirmDialog/>, <AlertDialog/>)
  // live in a single final return below and are therefore ALWAYS mounted,
  // regardless of which full-screen sub-view is active. Previously these
  // hosts were only rendered in the list-view return, so a Delete tapped
  // from the full-screen detail view flipped confirmState.isOpen=true but had
  // NO mounted host to render it — the "Are you sure?" dialog only appeared
  // later, when the user navigated back and the list-view return re-mounted
  // the host. Keeping the hosts unconditionally mounted makes the confirm
  // dialog appear immediately on the Delete tap, for every sub-view.
  let body;

  if (showDrillDetail && selectedDrill) {
    // Show full-screen drill detail view (read-only with audit trail)
    body = (
      <DrillDetailView
        drill={selectedDrill}
        onBack={handleBackFromDrillDetail}
        onEdit={(drill, reason) => {
          // Close detail view and open edit form with reason captured
          setDrillEditReason(reason);
          setShowDrillDetail(false);
          setEditingDrill(drill);
          setShowAddDrill(true);
        }}
        onDelete={() => handleDeleteDrill(selectedDrill?.id)}
        isDeleting={isDeletingDrill}
        onSignOff={async () => {
          // FT-06d P2: Sign off drill using approveDrill from useDrillsData
          const updatedDrill = await approveDrill(selectedDrill.id);
          if (updatedDrill) {
            setSelectedDrill(updatedDrill);
          }
          return !!updatedDrill;
        }}
      />
    );
  } else if (showAddDrill) {
    // If drill record form is open, show it instead of the module content
    body = (
      <DrillRecordFormClean
        isOpen={true}
        onClose={handleCloseRecordDrillForm}
        onSave={handleSaveDrill}
        vesselRequirements={vesselRequirements}
        preselectedDrillType={preselectedDrillType}
        initialData={editingDrill}
        isSaving={isSavingDrill}
        editReason={drillEditReason}
        canSignOff={canSignOff}
      />
    );
  } else if (showTrainingDetail && selectedTraining) {
    // Show full-screen training detail view (read-only with audit trail)
    body = (
      <TrainingDetailView
        training={selectedTraining}
        onBack={handleBackFromTrainingDetail}
        onEdit={(training, reason) => {
          // Close detail view and open edit form with reason captured
          setTrainingEditReason(reason);
          setShowTrainingDetail(false);
          setShowAddTraining(true);
        }}
        crew={crew}
      />
    );
  } else if (showAddTraining) {
    // Show full-screen training record view (create/edit form)
    body = (
      <RecordTrainingView
        onBack={handleBackFromRecordTraining}
        onSave={handleSaveTraining}
        crew={crew}
        initialData={selectedTraining}
        isSaving={isSavingTraining}
        editReason={trainingEditReason}
      />
    );
  } else if (showSettings) {
    // Show full-screen settings view if active
    body = (
      <DrillSettingsView
        onBack={handleBackFromDrillSettings}
      />
    );
  } else {
    body = (
      <div>
        {/* FT-387: header row — title + desktop Export button. Mobile export
            lives in the bottom bar (drills-export action); the bottom bar does
            NOT render at desktop width, so desktop needs its own affordance. */}
        <div className="mb-4 sm:mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Drills & Training</h1>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">Manage safety drills, training records, and compliance requirements</p>
          </div>
          <button
            type="button"
            data-testid="drills-export-desktop"
            onClick={handleExportReport}
            className="hidden lg:inline-flex items-center px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-700 transition-colors flex-shrink-0"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>

        <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="mt-6">
          {renderTabContent()}
        </div>

        {/* DrillDetailModal replaced by full-screen DrillDetailView - rendered above */}
        {/* DrillSettingsModal replaced by full-screen DrillSettingsView - rendered above */}

        <DrillExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          drills={visibleDrills}
          vesselInfo={currentVessel}
        />
      </div>
    );
  }

  return (
    <>
      {body}

      {/* FT-459: Custom dialog hosts are ALWAYS mounted (outside the
          view-switch above) so showConfirm/showAlert render immediately from
          any sub-view — including the full-screen drill detail Delete flow. */}
      <AlertDialog />
      <ConfirmDialog />
    </>
  );
};

export default DrillsModule;